import Route from '@ember/routing/route';
import { service } from '@ember/service';

import type { APIData, RequestError, Service } from 'webapp-types';

import type SourceType from 'webapp/models-custom/source-type';
import type AppDataLoaderMain from 'webapp/data-loader/main';

export default class DataManagerAutoConnectRoute extends Route {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('connect') declare connectService: Service.Connect;
	@service('store') declare storeService: Service.Store;

	override model(params: { source_key: string }): SourceType | void {
		const model = this.mapSourceKeyToSourceType(params.source_key);

		if (!model) {
			this.navigateToAvailable();
			return;
		}

		return model;
	}

	override afterModel(model: SourceType): void {
		const popup = this.navigationService.createFullscreenLoaderModal();
		popup.open({ text: `Connecting ${model.name}...` });

		let paramsString = window.location.search; // if we came to route directly
		if (!paramsString) {
			// if we got redirected
			const autoConnectUrl = (this.navigationService.dataLoader as AppDataLoaderMain)
				.autoConnectUrl;
			paramsString = '?' + (autoConnectUrl.split('?')[1] || '');
		}

		this.requestService
			.connectapi<APIData.ConnectApi.AutoConnectResponse>(
				'POST',
				`/auto-connect/${model.key}${paramsString}`,
				{},
			)
			.then((response) => {
				const sourceData = response?.datasource;
				if (sourceData) {
					// auto-connect bypasses the connect package, so no dataset flow is started for it
					const afterConnectAction = this.connectService.getAfterConnectAction({
						skipForDatasetDefaultSources: false,
					});
					this.storeService.pushPayload({ source: sourceData });
					this.navigationService.replaceRoute('datamanager.connected').then(() => {
						afterConnectAction(model, +sourceData.id);
					});
				} else {
					throw {
						code: 422,
						errors: [
							{
								code: 422,
								name: 'general',
								message: 'Invalid connect response!',
							},
						],
					};
				}
			})
			.catch((errorData: RequestError) => {
				void popup.close();
				this.navigateToAvailable();
				this.requestService.showAllValidationErrors(errorData);
			});
	}

	private mapSourceKeyToSourceType(sourceKey: string): SourceType | null {
		switch (sourceKey) {
			case 'LeadConnector':
				// LeadConnector is used as alias for GoHighLevel
				return this.mmSourceTypesService.findModelByKey('GoHighLevel');
			default:
				return this.mmSourceTypesService.findModelByKey(sourceKey);
		}
	}

	private navigateToAvailable(): void {
		this.navigationService.replaceRoute('datamanager.available');
	}
}
